import React, { useState } from 'react';
import clsx from 'clsx';

import Image from '@smart-tv/components/image';
import testIds from '@smart-tv/e2e/shared/variables';
import defaultMovieCover from '@smart-tv/scaffold/images/placeholder_default_portrait.svg';
import defaultSeriesCover from '@smart-tv/scaffold/images/placeholder_default_landscape.svg';
import { ContentCover, ContentLayout } from '@smart-tv/typings/content';

import { toModifier } from './content-item-helpers';
import styles from './content-item.module.scss';

interface OwnProps {
  coverType: ContentCover;
  cover: string;
  futureContent: boolean;
  liveTV: boolean;
  focused: boolean;
  animating: boolean;
  setAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  contentLayout: ContentLayout;
  imageAvailable?: boolean;
  image?: string;
  $isUnavailable?: boolean;
}

interface Props extends React.HTMLAttributes<HTMLDivElement>, OwnProps {}

export const ContentItemImage: React.FC<Props> = (props: Props) => {
  const {
    children,
    coverType,
    cover,
    liveTV,
    focused,
    animating,
    setAnimating,
    contentLayout,
    $isUnavailable
  } = props;

  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  const isPortraitLandscape =
    contentLayout === ContentLayout.Mixed && coverType === ContentCover.Portrait;

  const [imageAvailable, setImageAvailable] = React.useState(true);

  const imageSrc = hasError
    ? coverType === ContentCover.Portrait
      ? defaultMovieCover
      : defaultSeriesCover
    : cover;

  const handleError = () => {
    setImageAvailable(false);
    setHasError(true);
  };

  const contentItemImageClass = clsx(styles['image'], {
    [styles['image--unavailable']]: $isUnavailable
  });

  const coverMod = toModifier(coverType);

  const imageContentClass = clsx(styles['image-content'], styles[`image-content--${coverMod}`], {
    [styles['image-content--live-tv']]: liveTV,
    [styles['image-content--animating-focused']]: animating && focused,
    [styles['loaded']]: isImageLoaded
  });

  const dimmedBgClass = clsx(styles['portrait-dimmed-bg'], {
    [styles['portrait-dimmed-bg--image-available']]: imageAvailable,
    [styles['portrait-dimmed-bg--live-tv']]: liveTV,
    [styles['portrait-dimmed-bg--animating-focused']]: animating && focused,
    [styles['loaded']]: isImageLoaded
  });

  return (
    <div className={contentItemImageClass}>
      {!isPortraitLandscape ? (
        <Image
          src={imageSrc}
          onError={() => {
            handleError();
          }}
          onAnimationEnd={() => setAnimating(false)}
          data-testid={testIds.contentItem.image}
          onLoad={() => setIsImageLoaded(true)}
          className={imageContentClass}
        />
      ) : (
        <React.Fragment>
          <div
            className={dimmedBgClass}
            style={{ backgroundImage: `url(${cover}), url(${defaultSeriesCover})` }}
            onAnimationEnd={() => setAnimating(false)}
            data-testid={testIds.contentItem.image}
          />
          {imageAvailable && (
            <Image
              src={imageSrc}
              onError={() => {
                handleError();
              }}
              onLoad={() => setIsImageLoaded(true)}
              className={styles['inner-portrait']}
            />
          )}
        </React.Fragment>
      )}
      {children}
    </div>
  );
};

export default ContentItemImage;
